import { Permission } from '@backstage/plugin-permission-common';
import { policyEntityCreatePermission } from '@backstage-community/plugin-rbac-common';
import {
  catalogEntityCreatePermission,
  catalogEntityDeletePermission,
  catalogEntityReadPermission,
  catalogEntityRefreshPermission,
  catalogEntityValidatePermission,
  catalogLocationAnalyzePermission,
  catalogLocationCreatePermission,
  catalogLocationDeletePermission,
  catalogLocationReadPermission,
} from '@backstage/plugin-catalog-common/alpha';
import {
  pagerDutyAdminPermission,
  pagerDutyIncidentHistoryReadPermission,
} from './pagerduty';
import { feedbackReadAllPermission } from './feedback';
import { groupSlackChannelsPermission } from './slackChannels';
import { recommendedDocsManagePermission } from './recommendedDocs';
import {
  announcementCreatePermission,
  announcementUpdatePermission,
  announcementDeletePermission,
} from './announcements';

export type PermissionRegistryEntry = {
  permission: Permission;
  // Hone registers owned permissions with the backend permission registry.
  // External plugins register their own, so leave false to avoid double-registering.
  owned: boolean;
  // Backend plugin an owned permission registers under, so RBAC lists it under that plugin
  // in /rbac. The plugin must also be in permission.rbac.pluginsWithPermission.
  pluginId?: string;
  // Section header the permission sits under in the Permissions tab. Derived from the name when omitted.
  group?: string;
  // Optional sub-section header within a group (used for the Integrations group). Derived
  // for `hone.integrations.<service>` permissions when omitted; groups without one render flat.
  subgroup?: string;
  // Human label in the Settings > Permissions tab. Derived from the name when omitted.
  label?: string;
};

// The single registry of every permission the portal cares about. Add an entry here
// and it is registered with the backend (if owned) and shown in the Permissions tab —
// no other file needs editing.
export const permissionRegistry: PermissionRegistryEntry[] = [
  {
    permission: groupSlackChannelsPermission,
    owned: true,
    pluginId: 'catalog',
    group: 'Integrations',
    subgroup: 'Slack',
    label: 'Manage Slack channel mappings',
  },
  {
    permission: pagerDutyAdminPermission,
    owned: true,
    pluginId: 'pagerduty',
    group: 'Integrations',
    subgroup: 'PagerDuty',
    label: 'Administer PagerDuty',
  },
  {
    permission: pagerDutyIncidentHistoryReadPermission,
    owned: true,
    pluginId: 'pagerduty',
    group: 'Integrations',
    subgroup: 'PagerDuty',
    label: 'Read PagerDuty incident history',
  },
  {
    permission: feedbackReadAllPermission,
    owned: true,
    pluginId: 'entity-feedback',
    group: 'Feedback',
    label: 'View all entity feedback',
  },
  {
    permission: recommendedDocsManagePermission,
    owned: true,
    pluginId: 'recommended-docs',
    group: 'Documentation',
    label: 'Manage recommended documentation',
  },
  {
    permission: announcementCreatePermission,
    owned: false,
    group: 'Announcements',
    label: 'Create announcements',
  },
  {
    permission: announcementUpdatePermission,
    owned: false,
    group: 'Announcements',
    label: 'Update announcements',
  },
  {
    permission: announcementDeletePermission,
    owned: false,
    group: 'Announcements',
    label: 'Delete announcements',
  },
  {
    permission: policyEntityCreatePermission,
    owned: false,
    group: 'Access control',
    label: 'Manage RBAC roles & policies',
  },
  {
    permission: catalogEntityReadPermission,
    owned: false,
    group: 'Catalog',
    label: 'Read entities',
  },
  {
    permission: catalogEntityCreatePermission,
    owned: false,
    group: 'Catalog',
    label: 'Create entities',
  },
  {
    permission: catalogEntityDeletePermission,
    owned: false,
    group: 'Catalog',
    label: 'Delete entities',
  },
  {
    permission: catalogEntityRefreshPermission,
    owned: false,
    group: 'Catalog',
    label: 'Refresh entities',
  },
  {
    permission: catalogEntityValidatePermission,
    owned: false,
    group: 'Catalog',
    label: 'Validate entities',
  },
  {
    permission: catalogLocationReadPermission,
    owned: false,
    group: 'Catalog',
    label: 'Read locations',
  },
  {
    permission: catalogLocationCreatePermission,
    owned: false,
    group: 'Catalog',
    label: 'Create locations',
  },
  {
    permission: catalogLocationDeletePermission,
    owned: false,
    group: 'Catalog',
    label: 'Delete locations',
  },
  {
    permission: catalogLocationAnalyzePermission,
    owned: false,
    group: 'Catalog',
    label: 'Analyze locations',
  },
];

// Owned permissions grouped by the plugin they register under, so one backend module per
// plugin can register them where RBAC discovers them.
export const honeOwnedPermissionsByPlugin: Record<string, Permission[]> =
  permissionRegistry.reduce<Record<string, Permission[]>>((acc, entry) => {
    if (entry.owned && entry.pluginId) {
      (acc[entry.pluginId] ??= []).push(entry.permission);
    }
    return acc;
  }, {});

const humanize = (segment: string): string =>
  segment
    .split('-')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

// Group (section header) falls back to the area segment (`hone.<area>....`), else the
// first segment for external permissions.
export const permissionGroup = (entry: PermissionRegistryEntry): string => {
  if (entry.group) return entry.group;
  const parts = entry.permission.name.split('.');
  const area = parts[0] === 'hone' ? parts[1] : parts[0];
  return area ? humanize(area) : 'Other';
};

// Sub-section header within a group, or undefined for a flat row. Derived from the service
// segment of `hone.integrations.<service>....` permissions when omitted.
export const permissionSubgroup = (
  entry: PermissionRegistryEntry,
): string | undefined => {
  if (entry.subgroup) return entry.subgroup;
  const parts = entry.permission.name.split('.');
  if (parts[0] === 'hone' && parts[1] === 'integrations' && parts[2]) {
    return humanize(parts[2]);
  }
  return undefined;
};

// Label falls back to `<resource> <action>` derived from the last two name segments.
export const permissionLabel = (entry: PermissionRegistryEntry): string => {
  if (entry.label) return entry.label;
  const parts = entry.permission.name.split('.');
  const action = humanize(parts[parts.length - 1] ?? '');
  const resource = humanize(parts[parts.length - 2] ?? '');
  return resource ? `${resource} ${action}` : action;
};
